/**
 * Tasks that must not overlap, grouped by what they touch.
 *
 * One chain per key, run in the order they were added; different keys run
 * concurrently. The key for a row update is the row's id, which is the whole
 * point: two quick edits to the same cell issued in parallel can be applied by
 * the server in either order, and the one that lands second wins. Typing "ab"
 * then deleting the "b" would leave "ab" stored roughly one time in ten, and
 * nothing on screen would say so.
 *
 * Ported from upstream's `GroupTaskQueue`, minus the parts nothing uses yet.
 * Its `lock`/`release` exist so an edit to a row whose `POST` has not resolved
 * waits for the id -- worth having, and worth having when row creation lands
 * rather than now, with no caller and no test.
 */
export type TaskKey = string | number

export interface TaskQueue {
  /**
   * Runs a task after everything already queued under the same key.
   *
   * Several keys at once is how a write that spans rows -- a paste -- waits for
   * every row it touches and holds all of them until it is done. Without that
   * it would need a queue of its own, keyed by the table, and a paste could
   * then overlap a single-cell edit to a row inside it.
   */
  run: <T>(key: TaskKey | readonly TaskKey[], task: () => Promise<T>) => Promise<T>
}

const ignore = (): void => {}

export function createTaskQueue(): TaskQueue {
  // A key with nothing outstanding is removed rather than left holding a
  // resolved promise, so a table edited all afternoon does not accumulate an
  // entry per row touched.
  const chains = new Map<TaskKey, Promise<unknown>>()

  return {
    run<T>(key: TaskKey | readonly TaskKey[], task: () => Promise<T>): Promise<T> {
      const keys = Array.isArray(key) ? (key as readonly TaskKey[]) : [key as TaskKey]
      const previous = Promise.all(keys.map((k) => chains.get(k) ?? Promise.resolve()))
      const result = previous.then(task)

      // The chain carries on past a failure. One edit the server refuses must
      // not stop every later edit to the same row, which is what chaining the
      // rejection through would do.
      const chain: Promise<unknown> = result.then(ignore, ignore).then(() => {
        // Only the last task in a chain clears it. Anything queued behind this
        // one has already replaced the entry, and deleting it would let the
        // next task start alongside rather than after.
        for (const k of keys) if (chains.get(k) === chain) chains.delete(k)
      })
      for (const k of keys) chains.set(k, chain)

      return result
    },
  }
}
